import { isTokenValid } from "../common-functions/commonFunctions";
import { apiConfig } from "../constants/apiConfig";

export interface ISSECallbacks {
  onChunk: (content: string) => void;
  onError?: (error: string) => void;
  onDone?: () => void;
  onSessionId?: (sessionId: number, generationId?: string, cachedResponse?: string) => void;
}

const getAuthHeaders = (): Record<string, string> => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const getBuduBase = (): string =>
  (apiConfig.buduGPTBaseURL ?? "").replace(/\/$/, "");

const CHAT_FAILED_MESSAGE = "Unable to complete your request at the moment. Please try again";
const NETWORK_FAILED_MESSAGE = "Network error. Please check your internet connection and try again.";

export const streamBuduAgentPost = async (
  agentKey: string,
  body: object,
  callbacks: ISSECallbacks,
  signal?: AbortSignal,
): Promise<void> => {
  const { onChunk, onError, onDone, onSessionId } = callbacks;
  const url = `${getBuduBase()}/agents/${agentKey}/chat`;

  const onUnAuthorize = () => {
    localStorage.clear();
    sessionStorage.clear();
    alert("Session Expired! Please login again");
    window.location.href = "/Login";
  };

  try {
    if (!isTokenValid()) onUnAuthorize();

    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Timezone": Intl.DateTimeFormat().resolvedOptions().timeZone,
        ...getAuthHeaders(),
      },
      body: JSON.stringify(body),
      signal,
    });

    if (!response.ok || !response.body) {
      let message = CHAT_FAILED_MESSAGE;
      if (response.status === 404) {
        try {
          const errorBody = await response.clone().json();
          if (errorBody?.message === "Agent not found") {
            message = "The assistant is currently unavailable. Please contact your administrator.";
          }
        } catch {
        }
      }
      onError?.(message);
      return;
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const events = buffer.split("\n\n");
      buffer = events.pop() ?? "";

      for (const event of events) {
        const trimmed = event.trim();
        if (!trimmed.startsWith("data:")) continue;

        const payload = trimmed.slice(5).trim();

        if (payload === "[DONE]") {
          return;
        }

        try {
          const parsed = JSON.parse(payload);

          if (parsed.error) {
            onError?.(parsed.error);
          } else if (parsed.sessionId !== undefined) {
            onSessionId?.(parsed.sessionId, parsed.generationId, parsed.cachedResponse);
          } else if (parsed.content !== undefined) {
            onChunk(parsed.content);
          }
        } catch {
          // Ignore malformed SSE chunks
        }
      }
    }
  } catch (exception: any) {
    if (exception?.name === "AbortError") return;
    console.error("[BuduGPT stream error]", exception);
    onError?.(NETWORK_FAILED_MESSAGE);
  } finally {
    onDone?.();
  }
};

export const uploadChatFile = async (
  file: File,
  sessionId?: number | null,
  sourceType?: string
): Promise<{ filename: string; uploadId: string }> => {
  const url = `${getBuduBase()}/ask-me/upload`;
  const formData = new FormData();
  formData.append("file", file);
  if (sessionId) {
    formData.append("session_id", sessionId.toString());
  }
  if (sourceType) {
    formData.append("source_type", sourceType);
  }

  const response = await fetch(url, {
    method: "POST",
    headers: getAuthHeaders(),
    body: formData,
  });

  if (!response.ok) {
    throw new Error(`Upload failed with status ${response.status}`);
  }

  const json = await response.json();
  return json.data as { filename: string; uploadId: string };
};

export const removeChatFile = async (uploadId: string): Promise<void> => {
  const url = `${getBuduBase()}/ask-me/upload/${uploadId}`;
  await fetch(url, {
    method: "DELETE",
    headers: getAuthHeaders(),
  });
};

export const submitMessageFeedback = async (
  sessionId: number,
  messageIndex: number,
  rating: number,
  userQuestion?: string,
  chatResponse?: string,
  correctionText?: string,
): Promise<void> => {
  const url = `${getBuduBase()}/agents/feedback`;
  await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...getAuthHeaders(),
    },
    body: JSON.stringify({ sessionId, messageIndex, rating, userQuestion, chatResponse, correctionText }),
  });
};
